import React, { useEffect } from 'react';
import 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './cookieConsent.css';
import { useTranslationsContext } from './useTranslations';
import { LocaleContext } from './layout';
import { CONSENT_GRANTED_EVENT, CONSENT_REVOKED_EVENT } from './externalEmbed';
import { MARKETING_CONSENT_GRANTED_EVENT, MARKETING_CONSENT_REVOKED_EVENT } from './metaPixel';

export const CONSENT_MANAGER_READY_EVENT = 'cookie-consent-manager-ready';

export const CookieConsent: React.FC = () => {
  const { locale } = React.useContext(LocaleContext);

  useEffect(() => {
    // Build locale-aware paths for cookie consent links
    const lang = locale.split('-')[0];
    const prefix = lang === 'de' ? '' : `/${lang}`;
    const privacyLink = `${prefix}/privacy-policy`;
    const contactLink = `${prefix}/contact`;

    if (!document.getElementById('cc--main')) {
      window.CC = window.initCookieConsent();

      window.CC.run({
        revision: 2,
        current_lang: locale.split('-')[0],
        autoclear_cookies: true,
        force_consent: true,
        page_scripts: true,

        gui_options: {
          consent_modal: {
            layout: 'box', // box/cloud/bar
            position: 'middle center', // bottom/middle/top + left/right/center
            transition: 'zoom', // zoom/slide
            swap_buttons: true // enable to invert buttons
          }
        },

        onFirstAction: function () {},

        onAccept: function () {
          if (window.CC.allowedCategory('external_media')) {
            window.dispatchEvent(new CustomEvent(CONSENT_GRANTED_EVENT));
          }
          if (window.CC.allowedCategory('marketing')) {
            window.dispatchEvent(new CustomEvent(MARKETING_CONSENT_GRANTED_EVENT));
          }
        },

        onChange: function () {
          if (window.CC.allowedCategory('external_media')) {
            window.dispatchEvent(new CustomEvent(CONSENT_GRANTED_EVENT));
          } else {
            window.dispatchEvent(new CustomEvent(CONSENT_REVOKED_EVENT));
          }
          if (window.CC.allowedCategory('marketing')) {
            window.dispatchEvent(new CustomEvent(MARKETING_CONSENT_GRANTED_EVENT));
          } else {
            window.dispatchEvent(new CustomEvent(MARKETING_CONSENT_REVOKED_EVENT));
          }
        },

        languages: {
          de: {
            consent_modal: {
              title: 'Cookies? 🍪',
              description:
                `Wir verwenden Cookies und andere Technologien auf unserer Website. Einige von ihnen sind essenziell, während andere uns helfen, diese Website und Ihre Erfahrung zu verbessern. Personenbezogene Daten können verarbeitet werden (z. B. IP-Adressen), z. B. für personalisierte Anzeigen und Inhalte oder Anzeigen- und Inhaltsmessung. Weitere Informationen über die Verwendung Ihrer Daten finden Sie in unserer <a href="${privacyLink}" class="cc-link">Datenschutzerklärung</a>. Sie können Ihre Auswahl jederzeit unter Einstellungen widerrufen oder anpassen.`,
              primary_btn: {
                text: 'Alle akzeptieren',
                role: 'accept_all' // 'accept_selected' or 'accept_all'
              },
              secondary_btn: {
                text: 'Einstellungen',
                role: 'settings' // 'settings' or 'accept_necessary'
              }
            },
            settings_modal: {
              title: 'Cookie-Einstellungen',
              save_settings_btn: 'Einstellungen speichern',
              accept_all_btn: 'Alle akzeptieren',
              reject_all_btn: 'Alle ablehnen',
              close_btn_label: 'Schließen',
              // cookie_table_caption: 'Cookie list',
              cookie_table_headers: [
                { col1: 'Name' },
                { col2: 'Domain' },
                { col3: 'Ablauf' },
                { col4: 'Beschreibung' }
              ],
              blocks: [
                {
                  title: 'Cookie Nutzung',
                  description:
                    `Wir verwenden Cookies und andere Technologien auf unserer Website. Einige von ihnen sind essenziell, während andere uns helfen, diese Website und Ihre Erfahrung zu verbessern. Personenbezogene Daten können verarbeitet werden (z. B. IP-Adressen), z. B. für personalisierte Anzeigen und Inhalte oder Anzeigen- und Inhaltsmessung. Weitere Informationen über die Verwendung Ihrer Daten finden Sie in unserer <a href="${privacyLink}" class="cc-link">Datenschutzerklärung</a>. Sie können Ihre Auswahl jederzeit unter Einstellungen widerrufen oder anpassen.`
                },
                {
                  title: 'Notwendige Cookies',
                  description:
                    'Essenzielle Cookies ermöglichen grundlegende Funktionen und sind für die einwandfreie Funktion der Website erforderlich.',
                  toggle: {
                    value: 'necessary',
                    enabled: true,
                    readonly: true // cookie categories with readonly=true are all treated as "necessary cookies"
                  },
                  cookie_table: [
                    {
                      col1: 'acm_newsletter_prompt',
                      col2: 'autocenter-meschede.de',
                      col3: '30 Tage / 1 Jahr',
                      col4: 'Speichert, ob das Newsletter-Popup geschlossen oder die Anmeldung bestätigt wurde'
                    }
                  ]
                },
                {
                  title: 'Statistik Cookies',
                  description:
                    'Statistik Cookies erfassen Informationen anonym. Diese Informationen helfen uns zu verstehen, wie unsere Besucher unsere Website nutzen.',
                  toggle: {
                    value: 'analytics', // your cookie category
                    enabled: false,
                    readonly: false
                  },
                  cookie_table: [
                    // list of all expected cookies
                    {
                      col1: '^_ga', // match all cookies starting with "_ga"
                      col2: 'google.com',
                      col3: '2 years',
                      col4: 'Google Analytics 4',
                      is_regex: true
                    },
                    {
                      col1: '_gid',
                      col2: 'google.com',
                      col3: '1 day',
                      col4: 'Google Analytics 4'
                    }
                  ]
                },
                {
                  title: 'Externe Medien',
                  description:
                    'Inhalte von Videoplattformen und Social-Media-Plattformen werden standardmäßig blockiert. Wenn Cookies von externen Medien akzeptiert werden, bedarf der Zugriff auf diese Inhalte keiner manuellen Einwilligung mehr.',
                  toggle: {
                    value: 'external_media', // your cookie category
                    enabled: false,
                    readonly: false
                  },
                  cookie_table: [
                    // list of all expected cookies
                    {
                      col1: 'cc_youtube',
                      col2: 'autocenter-meschede.de',
                      col3: '1 year',
                      col4: 'Speichert die Zustimmung zur Anzeige von Youtube-Inhalten'
                    }
                  ]
                },
                {
                  title: 'Marketing Cookies',
                  description:
                    'Marketing-Cookies werden verwendet, um Besuchern auf Websites zu folgen. Sie ermöglichen es, relevante Werbeanzeigen für den einzelnen Nutzer anzuzeigen und zu messen. Wir setzen das Meta Pixel (Facebook Pixel) ein, um Conversions zu messen und relevante Werbung auf Meta-Plattformen auszuspielen.',
                  toggle: {
                    value: 'marketing',
                    enabled: false,
                    readonly: false
                  },
                  cookie_table: [
                    {
                      col1: '_fbp',
                      col2: 'meta.com',
                      col3: '90 days',
                      col4: 'Meta Pixel – wird zur Identifizierung von Browsern für die Werbeauslieferung verwendet'
                    },
                    {
                      col1: '_fbc',
                      col2: 'meta.com',
                      col3: '90 days',
                      col4: 'Meta Pixel – speichert Click-IDs aus Facebook-Anzeigen'
                    }
                  ]
                },
                {
                  title: 'Weitere Informationen',
                  description:
                    `Wenn Sie Fragen zu unserer Cookie-Politik und Ihren Wahlmöglichkeiten haben, <a class="cc-link" href="${contactLink}">wenden Sie sich bitte an uns</a>.`
                }
              ]
            }
          },
          en: {
            consent_modal: {
              title: ' Cookies? 🍪',
              description:
                `We use cookies and other technologies on our website. Some of them are essential, while others help us improve this website and your experience. Personal data may be processed (e.g., IP addresses), for example, for personalized ads and content, or ad and content measurement. For more information about how we use your data, please see our <a href="${privacyLink}" class="cc-link">Privacy Policy</a>. You can revoke or adjust your selection at any time under Settings.`,
              primary_btn: {
                text: 'Accept All',
                role: 'accept_all' // 'accept_selected' or 'accept_all'
              },
              secondary_btn: {
                text: 'Settings',
                role: 'settings' // 'settings' or 'accept_necessary'
              }
            },
            settings_modal: {
              title: 'Cookie Settings',
              save_settings_btn: 'Save Settings',
              accept_all_btn: 'Accept All',
              reject_all_btn: 'Reject All',
              close_btn_label: 'Close',
              // cookie_table_caption: 'Cookie list',
              cookie_table_headers: [
                { col1: 'Name' },
                { col2: 'Domain' },
                { col3: 'Expiration' },
                { col4: 'Description' }
              ],
              blocks: [
                {
                  title: 'Cookie Usage',
                  description:
                    `We use cookies and other technologies on our website. Some of them are essential, while others help us improve this website and your experience. Personal data may be processed (e.g., IP addresses), for example, for personalized ads and content, or ad and content measurement. For more information about how we use your data, please see our <a href="${privacyLink}" class="cc-link">Privacy Policy</a>. You can revoke or adjust your selection at any time under Settings.`
                },
                {
                  title: 'Necessary Cookies',
                  description:
                    'Essential cookies enable basic functionality and are required for the website to function properly.',
                  toggle: {
                    value: 'necessary',
                    enabled: true,
                    readonly: true // cookie categories with readonly=true are all treated as "necessary cookies"
                  },
                  cookie_table: [
                    {
                      col1: 'acm_newsletter_prompt',
                      col2: 'autocenter-meschede.de',
                      col3: '30 days / 1 year',
                      col4: 'Stores whether the newsletter popup was dismissed or the subscription was confirmed'
                    }
                  ]
                },
                {
                  title: 'Statistics Cookies',
                  description:
                    'Statistics cookies collect information anonymously. This information helps us understand how our visitors use our website.',
                  toggle: {
                    value: 'analytics', // your cookie category
                    enabled: false,
                    readonly: false
                  },
                  cookie_table: [
                    // list of all expected cookies
                    {
                      col1: '^_ga', // match all cookies starting with "_ga"
                      col2: 'google.com',
                      col3: '2 years',
                      col4: 'Google Analytics 4',
                      is_regex: true
                    },
                    {
                      col1: '_gid',
                      col2: 'google.com',
                      col3: '1 day',
                      col4: 'Google Analytics 4'
                    }
                  ]
                },
                {
                  title: 'External Media',
                  description:
                    'Content from video platforms and social media platforms is blocked by default. If cookies from external media are accepted, access to this content no longer requires manual consent.',
                  toggle: {
                    value: 'external_media', // your cookie category
                    enabled: false,
                    readonly: false
                  },
                  cookie_table: [
                    // list of all expected cookies
                    {
                      col1: 'cc_youtube',
                      col2: 'autocenter-meschede.de',
                      col3: '1 year',
                      col4: 'Saves the consent to display Youtube content'
                    }
                  ]
                },
                {
                  title: 'Marketing Cookies',
                  description:
                    'Marketing cookies are used to track visitors across websites. They allow us to display relevant ads for individual users and to measure conversions. We use the Meta Pixel (Facebook Pixel) to measure conversions and serve relevant ads on Meta platforms.',
                  toggle: {
                    value: 'marketing',
                    enabled: false,
                    readonly: false
                  },
                  cookie_table: [
                    {
                      col1: '_fbp',
                      col2: 'meta.com',
                      col3: '90 days',
                      col4: 'Meta Pixel – used to identify browsers for ad delivery'
                    },
                    {
                      col1: '_fbc',
                      col2: 'meta.com',
                      col3: '90 days',
                      col4: 'Meta Pixel – stores click IDs from Facebook ads'
                    }
                  ]
                },
                {
                  title: 'More information',
                  description:
                    `For any queries in relation to our policy on cookies and your choices, please <a class="cc-link" href="${contactLink}">contact us</a>.`
                }
              ]
            }
          },
          fr: {
            consent_modal: {
              title: ' Cookies? 🍪',
              description:
                `Nous utilisons des cookies et d'autres technologies sur notre site web. Certains d'entre eux sont essentiels, tandis que d'autres nous aident à améliorer ce site et votre expérience. Les données personnelles peuvent être traitées (par exemple, les adresses IP), par exemple pour des annonces et des contenus personnalisés ou pour mesurer les annonces et les contenus. Vous trouverez de plus amples informations sur l'utilisation de vos données dans notre <a href="${privacyLink}" class="cc-link">déclaration de confidentialité</a>. Vous pouvez révoquer ou adapter votre choix à tout moment sous Paramètres`,
              primary_btn: {
                text: 'Accepter tout',
                role: 'accept_all' // 'accept_selected' or 'accept_all'
              },
              secondary_btn: {
                text: 'Paramètres des cookies',
                role: 'settings' // 'settings' or 'accept_necessary'
              }
            },
            settings_modal: {
              title: 'Paramètres des cookies',
              save_settings_btn: 'Enregistrer les paramètres',
              accept_all_btn: 'Accepter tout',
              reject_all_btn: 'Refuser tout',
              close_btn_label: 'Fermer',
              // cookie_table_caption: 'Cookie list',
              cookie_table_headers: [
                { col1: 'Nom' },
                { col2: 'Domaine' },
                { col3: 'Expiration' },
                { col4: 'Description' }
              ],
              blocks: [
                {
                  title: 'Utilisation du cookie',
                  description:
                    `Nous utilisons des cookies et d'autres technologies sur notre site web. Certains d'entre eux sont essentiels, tandis que d'autres nous aident à améliorer ce site et votre expérience. Les données personnelles peuvent être traitées (par exemple, les adresses IP), par exemple pour des annonces et des contenus personnalisés ou pour mesurer les annonces et les contenus. Vous trouverez de plus amples informations sur l'utilisation de vos données dans notre <a href="${privacyLink}" class="cc-link">déclaration de confidentialité</a>. Vous pouvez révoquer ou adapter votre choix à tout moment sous Paramètres`
                },
                {
                  title: 'Cookies nécessaires',
                  description:
                    'Les cookies essentiels permettent des fonctions de base et sont nécessaires au bon fonctionnement du site web',
                  toggle: {
                    value: 'necessary',
                    enabled: true,
                    readonly: true // cookie categories with readonly=true are all treated as "necessary cookies"
                  },
                  cookie_table: [
                    {
                      col1: 'acm_newsletter_prompt',
                      col2: 'autocenter-meschede.de',
                      col3: '30 jours / 1 an',
                      col4: "Enregistre si la fenêtre de newsletter a été fermée ou si l'inscription a été confirmée"
                    }
                  ]
                },
                {
                  title: 'Cookies de statistiques',
                  description:
                    'Les cookies statistiques collectent des informations de manière anonyme. Ces informations nous aident à comprendre comment nos visiteurs utilisent notre site web',
                  toggle: {
                    value: 'analytics', // your cookie category
                    enabled: false,
                    readonly: false
                  },
                  cookie_table: [
                    // list of all expected cookies
                    {
                      col1: '^_ga', // match all cookies starting with "_ga"
                      col2: 'google.com',
                      col3: '2 years',
                      col4: 'Google Analytics 4',
                      is_regex: true
                    },
                    {
                      col1: '_gid',
                      col2: 'google.com',
                      col3: '1 day',
                      col4: 'Google Analytics 4'
                    },
                  ]
                },
                {
                  title: 'Médias externes',
                  description:
                    'Le contenu des plateformes vidéo et des plateformes de médias sociaux est bloqué par défaut. Si les cookies des médias externes sont acceptés, l\'accès à ce contenu ne nécessite plus de consentement manuel.',
                  toggle: {
                    value: 'external_media', // your cookie category
                    enabled: false,
                    readonly: false
                  },
                  cookie_table: [
                    // list of all expected cookies
                    {
                      col1: 'cc_youtube',
                      col2: 'autocenter-meschede.de',
                      col3: '1 year',
                      col4: 'Enregistre le consentement pour afficher le contenu Youtube'
                    }
                  ]
                },
                {
                  title: 'Cookies marketing',
                  description:
                    'Les cookies marketing sont utilisés pour suivre les visiteurs sur les sites web. Ils nous permettent d\'afficher des publicités pertinentes pour chaque utilisateur et de mesurer les conversions. Nous utilisons le Meta Pixel (Facebook Pixel) pour mesurer les conversions et diffuser des publicités pertinentes sur les plateformes Meta.',
                  toggle: {
                    value: 'marketing',
                    enabled: false,
                    readonly: false
                  },
                  cookie_table: [
                    {
                      col1: '_fbp',
                      col2: 'meta.com',
                      col3: '90 jours',
                      col4: 'Meta Pixel – utilisé pour identifier les navigateurs pour la diffusion de publicités'
                    },
                    {
                      col1: '_fbc',
                      col2: 'meta.com',
                      col3: '90 jours',
                      col4: 'Meta Pixel – stocke les identifiants de clics provenant des publicités Facebook'
                    }
                  ]
                },
                {
                  title: "Plus d'informations",
                  description:
                    `Pour toute question relative à notre politique en matière de cookies et à vos choix, veuillez <a class="cc-link" href="${contactLink}">nous contacter</a>.`
                }
              ]
            }
          }
        }
      });

      window.dispatchEvent(new CustomEvent(CONSENT_MANAGER_READY_EVENT));
    }
  }, []);

  return null;
};
